import { NumberField as BaseNumberField } from '@base-ui-components/react/number-field';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactNode, useId } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';

export interface NumberFieldProps extends BaseNumberField.Root.Props {
  label?: ReactNode;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: ReactNode;
  fullWidth?: boolean;
}

export default function NumberField({
  id: idProp,
  label,
  error,
  size = 'small',
  helperText,
  fullWidth,
  ...other
}: NumberFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  return (
    <BaseNumberField.Root
      {...other}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          fullWidth={fullWidth}
          variant="outlined"
        >
          {props.children}
        </FormControl>
      )}
    >
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <BaseNumberField.Input
        id={id}
        onBlur={other.onBlur}
        onKeyUp={other.onKeyUp}
        onKeyDown={other.onKeyDown}
        onFocus={other.onFocus}
        render={(props, state) => (
          <OutlinedInput
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            slotProps={{
              input: {
                ...props,
                'aria-invalid': !!error,
              },
            }}
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  flexDirection: 'column',
                  maxHeight: 'unset',
                  alignSelf: 'stretch',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  ml: 0,
                  '& button': {
                    py: 0,
                    flex: 1,
                    borderRadius: 0.5,
                  },
                }}
              >
                <BaseNumberField.Increment
                  aria-controls={id}
                  render={<IconButton size={size} aria-label="Increase" />}
                >
                  <KeyboardArrowUpIcon fontSize={size} sx={{ transform: 'translateY(2px)' }} />
                </BaseNumberField.Increment>

                <BaseNumberField.Decrement
                  aria-controls={id}
                  render={<IconButton size={size} aria-label="Decrease" />}
                >
                  <KeyboardArrowDownIcon fontSize={size} sx={{ transform: 'translateY(-2px)' }} />
                </BaseNumberField.Decrement>
              </InputAdornment>
            }
            sx={{ pr: 0 }}
          />
        )}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </BaseNumberField.Root>
  );
}
